import { Avatar, Box, Button, Flex, Heading, Spacer, Stack, Text } from '@chakra-ui/react'
import React from 'react'

import UserQ from '@/queries/UserQ'
import type { Query } from '@/client/query'

import ModalV3 from '@/components/UI/Modal/ModalV3'

import ProfileItemContainer from './ProfileItemContainer'
import { courseCertificationItemsFromQuery } from './transforms'
import type { CourseCertificationItem } from './types'

const SHOW_ALL_LIMIT = 5

function CertificationList({
  certificationItems,
}: {
  certificationItems: CourseCertificationItem[]
}): React.ReactElement {
  return (
    <Stack direction="column" spacing={2}>
      {certificationItems.map(({ name, icon, label }) => (
        <Stack
          key={name}
          direction="row"
          spacing={4}
          align="center"
          width="100%"
          borderRadius="8px"
          py={2}
        >
          <Avatar name={name} width="32px" height="32px" borderRadius="4px" src={icon} />
          <Text as="span" fontWeight={600}>
            {name}
          </Text>
          <Spacer />
          <Text as="span" color="fg.muted" fontWeight={600} fontSize="14px" textAlign="right">
            {label}
          </Text>
        </Stack>
      ))}
    </Stack>
  )
}

export default function ProfileCertifications({ user }: { user: Query }): JSX.Element {
  const firstName = user(UserQ.first_name) ?? ''
  const [showAll, setShowAll] = React.useState(false)

  const certificationItems = courseCertificationItemsFromQuery({ user })

  return (
    <Box>
      <Heading size="xl" mb={6}>
        Certifications
      </Heading>
      <ProfileItemContainer>
        {certificationItems.length === 0 ? (
          <Flex justify="center">
            <Box color="fg.muted">{firstName} doesn&apos;t have any certifications yet.</Box>
          </Flex>
        ) : null}
        <CertificationList certificationItems={certificationItems.slice(0, SHOW_ALL_LIMIT)} />
        {certificationItems.length < SHOW_ALL_LIMIT ? null : (
          <>
            <ModalV3
              onClose={() => setShowAll(false)}
              open={showAll}
              size="lg"
              title={() => <Heading size="xl">Certifications</Heading>}
              body={() => <CertificationList certificationItems={certificationItems} />}
            />
            <Box mt={2}>
              <Button variant="ghost" onClick={() => setShowAll(true)}>
                Show More
              </Button>
            </Box>
          </>
        )}
      </ProfileItemContainer>
    </Box>
  )
}
